import './on-demand-view.css';

import h from 'hyperscript';

import { on, unByKey } from 'cadenza/utils/event-util';
import { deleteUrlParam, setParam } from 'cadenza/utils/url-param-utils';
import {
  CONDITION_CHANGE,
  CONDITIONS_HIERARCHIES_CHANGE,
  CHANGED,
  APPLY_FILTERS
} from 'cadenza/conditions/event-types';
import { HIERARCHY_CHANGE } from 'cadenza/hierarchy-selection/event-types';
import { EXECUTION_CONFIRMED } from 'cadenza/api-client/api-constants';
import { mediaXsOnly } from 'cadenza/utils/media';
import { bindHotkey, getFormattedHotkey, unbindHotkey } from 'cadenza/hotkeys/hotkeys';
import { HOTKEYS } from 'cadenza/hotkeys/hotkey-definitions';

import i18n from './on-demand-view.properties';

const CLASS_NAME = 'd-on-demand-view';
export const COVER_VIEW_CLASS_NAME = 'd-on-demand-canceled-cover';

const ON_DEMAND_UPDATE_CLASS_SELECTOR = '.d-on-demand-view--update';
const ON_DEMAND_FIRST_LOAD_SELECTOR = '.d-on-demand-view--first-load';
const REPEAT_CANCELED_CLASS_SELECTOR = '.d-on-demand-view--repeatCanceled';

/**
 * @typedef OnDemandState
 * @type {object}
 * @property NEW - nothing loaded yet.
 * @property OUTDATED - displaying something but already loading something new.
 * @property CANCELED - latest request are cancelled.
 */

/** @type {OnDemandState} */
const State = {
  NEW: 'NEW',
  OUTDATED: 'OUTDATED',
  CANCELED: 'CANCELED'
};

export class OnDemandView extends HTMLElement {

  #refreshHandler;
  #filterController;
  #eventListenerKeys = [];
  _cancelState = new CancelState(this);

  /**
   * onDemand mode means that every filter change has to be confirmed.
   * If false, the OnDemandView is only displayed after a manual cancel via button.
   *
   * @return {boolean} Whether we're in on-demand mode
   */
  get _onDemandMode () {
    return this.cancelOnly !== 'true' || mediaXsOnly.matches;
  }

  get cancelOnly () {
    return this.getAttribute('cancel-only');
  }

  set cancelOnly (cancelOnly) {
    this.setAttribute('cancel-only', cancelOnly);
  }

  get hiddenElementSelector () {
    return this.getAttribute('hidden-element-selector');
  }

  set hiddenElementSelector (value) {
    this.setAttribute('hidden-element-selector', value);
  }

  set _state (state) {
    this.dataset.state = state;
  }

  get _state () {
    return this.dataset.state || State.NEW;
  }

  connectedCallback () {
    // allow to accumulate information carried by throttled events, when execution is confirmed it must be cleaned
    this.changedHierarchies = new Set();
    this.changedConditions = new Set();

    this.classList.add(CLASS_NAME, COVER_VIEW_CLASS_NAME);

    if (this._onDemandMode) {
      const executionConfirmed = this._executionConfirmed;
      this.hidden = executionConfirmed;
      if (executionConfirmed) {
        this._setStateOutdated();
      }
    } else {
      this.hidden = true;
    }

    this._registerEvents(this._filterController || this);
    this.append(this._createUiContent());
    this._activateKeyListener();
    this._registerMobileControlsEvents();
  }

  disconnectedCallback () {
    unbindHotkey(HOTKEYS.SELECTORS_UPDATE_ON_DEMAND_VIEW);
  }

  _registerMobileControlsEvents () {
    if (!this._onDemandMode || !this._filterController) {
      return;
    }
    const applyButton = document.querySelector('.button-primary.mobile-filter');
    const cancelButton = document.querySelector('.button-secondary.mobile-filter');
    if (!applyButton || !cancelButton) {
      return;
    }
    const sidebarGetter = () => this._filterController.closest('.d-sidebar');
    this.#eventListenerKeys.push(
      on(applyButton, 'click', _ => {
        this._onLoadData();
        sidebarGetter().close();
      }),
      on(cancelButton, 'click', _ => sidebarGetter().close())
    );
  }

  _activateKeyListener () {
    bindHotkey({
      hotkeyId: HOTKEYS.SELECTORS_UPDATE_ON_DEMAND_VIEW,
      callback: event => {
        event.preventDefault();
        if (!this.hidden) {
          this._onLoadData();
        }
      }
    });
  }

  /**
   * If the filter controller is not available yet when the OnDemandView is added to the DOM, it can
   * be set explicitly later with this method.
   *
   * @param filterController - filterController to use as event source
   */
  setFilterController (filterController) {
    this.#filterController = filterController;
    unByKey(this.#eventListenerKeys);
    this._registerEvents(this.#filterController);
    this._registerMobileControlsEvents();
  }

  get _filterController () {
    return this.#filterController || document.querySelector('.d-filter-controller');
  }

  _registerEvents (eventSource) {
    this.#eventListenerKeys = [
      on(eventSource, [
        CONDITION_CHANGE,
        HIERARCHY_CHANGE,
        CONDITIONS_HIERARCHIES_CHANGE
      ], event => this._filterCriteriaChanged(event)),
      on(eventSource, APPLY_FILTERS,
        event => this._onLoadData()),
      on(document, 'keydown', event => {
        if (event.ctrlKey && event.key === 'Enter') {
          this._onLoadData();
          event.preventDefault();
        }
      })
    ];
  }

  setEnabled (enabled) {
    [ ...this.querySelectorAll('button') ]
      .forEach(button => (button.disabled = !enabled));
  }

  handleCanceledRequest (refreshHandler, conditionValueSetHash) {
    this._cancelState.requestCanceled(conditionValueSetHash);
    // ignore cancel, if we already have a new request
    if (!this._cancelState.canceled) {
      return;
    }
    this._state = State.CANCELED;
    this.#refreshHandler = refreshHandler;
    this.hidden = false;
  }

  get _executionConfirmed () {
    return new URLSearchParams(location.search).get(EXECUTION_CONFIRMED) === 'true';
  }

  _createUiContent () {
    this._firstLoadHeader = h('span.headline' + ON_DEMAND_FIRST_LOAD_SELECTOR, i18n('onDemand.initial.text'));
    this._updateHeader = h('span.headline' + ON_DEMAND_UPDATE_CLASS_SELECTOR, i18n('onDemand.update.text'));
    this._repeatCanceledHeader = h('span.headline' + REPEAT_CANCELED_CLASS_SELECTOR, i18n('repeatCanceled.text'));
    this._firstLoadButton = h('button.button.button-primary' + ON_DEMAND_FIRST_LOAD_SELECTOR, {
      type: 'button',
      title: i18n('onDemand.initial.btn'),
      onclick: () => this._onLoadData(false) // do not mark as outdated on initial load
    }, i18n('onDemand.initial.btn'));
    this._updateButton = h('button.button.button-primary' + ON_DEMAND_UPDATE_CLASS_SELECTOR, {
      type: 'button',
      title: i18n('onDemand.update.btn'),
      onclick: () => this._onLoadData()
    }, i18n('onDemand.update.btn'));
    this._repeatCanceledButton = h('button.button.button-primary' + REPEAT_CANCELED_CLASS_SELECTOR, {
      type: 'button',
      title: i18n('repeatCanceled.btn'),
      onclick: () => {
        this._onLoadData();
        this._cancelState.reset();
      }
    }, i18n('repeatCanceled.btn'));

    return h('span#confirmExecutionContainer',
      h('div#confirmExecutionOverlay.dimmed-backdrop',
        this._firstLoadHeader,
        this._updateHeader,
        this._repeatCanceledHeader,
        this._firstLoadButton,
        this._updateButton,
        this._repeatCanceledButton,
        h('span.text-muted', `(${getFormattedHotkey(HOTKEYS.SELECTORS_UPDATE_ON_DEMAND_VIEW)})`)
      ));
  }

  _filterCriteriaChanged (event) {
    if (event.type === CONDITION_CHANGE) {
      this.changedConditions.add(event.detail.conditionId);
      return;
    }
    if (event.type === HIERARCHY_CHANGE) {
      this.changedHierarchies.add(event.detail.name);
      return;
    }
    // the event can contain an oldConditionValuesSetHash if a condition changed, but not if a hierarchy changed
    const oldConditionValuesSetHash = event.detail.oldConditionValuesSetHash || this.conditionValuesSetHash || this._getConditionValuesSetHash();
    if (event.detail.conditionValuesSetHash) {
      this.conditionValuesSetHash = event.detail.conditionValuesSetHash;
    }

    if (!event.cancelable) {
      this.hidden = true;
      return;
    }

    event.detail.hierarchyNames.forEach(value => this.changedHierarchies.add(value));
    event.detail.conditionIds.forEach(value => this.changedConditions.add(value));

    if (this._onDemandMode) {
      deleteUrlParam(EXECUTION_CONFIRMED);
      this.hidden = false;
      this._cancelState.requestCanceled(oldConditionValuesSetHash);
      this._setStateOutdated();

      event.preventDefault();
    }
  }

  _setStateOutdated () {
    this._state = State.OUTDATED;
  }

  /**
   * On loading data
   *
   * @param markAsOutdated - whether this leaves the NEW state.
   * False for the initial request, true for filterChanges and loading changed filters.
   * @param oldConditionValuesSetHash - the conditionValuesSetHash before condition changes
   */
  _onLoadData (markAsOutdated = true, oldConditionValuesSetHash = null) {
    if (this._onDemandMode) {
      setParam({ paramName: EXECUTION_CONFIRMED, paramValue: 'true' });
    }
    if (this._state === State.NEW) {
      this._showHiddenElement();
      this._dispatchChangeEvent(oldConditionValuesSetHash);
    } else if (this._state === State.OUTDATED) {
      this._dispatchChangeEvent(oldConditionValuesSetHash);
    } else /* if (this._state === State.CANCELED) */ {
      if (this._cancelState.canceled) {
        // enable going back to this state later
        this._cancelState.reset();
        this._refresh();
      }
    }
    if (markAsOutdated) {
      this._setStateOutdated();
    }
  }

  /**
   * Hide overlay and execute refresh handler.
   */
  _refresh () {
    this.hidden = true;
    // refresh the sourrounding settings
    this.#refreshHandler();
    this.#refreshHandler = null;
  }

  _getConditionValuesSetHash () {
    return this.conditionValuesSetHash || this._filterController
      ? this._filterController.conditionValuesSetHash
      : null;
  }

  _dispatchChangeEvent (oldConditionValuesSetHash = null) {
    const eventTarget = this._filterController || this;
    const eventDetail = {
      conditionValuesSetHash: this._getConditionValuesSetHash(),
      conditionIds: Array.from(this.changedConditions),
      hierarchyNames: Array.from(this.changedHierarchies),
      ...oldConditionValuesSetHash ? { oldConditionValuesSetHash } : null
    };
    eventTarget.dispatchEvent(new CustomEvent(CONDITIONS_HIERARCHIES_CHANGE, { detail: eventDetail }));
    eventTarget.dispatchEvent(new CustomEvent(CHANGED, { detail: eventDetail }));

    this.changedConditions.clear();
    this.changedHierarchies.clear();
  }

  _showHiddenElement () {
    const hiddenElement = document.querySelector(this.hiddenElementSelector);
    if (hiddenElement) {
      hiddenElement.hidden = false;
    }
  }

}

class CancelState {

  _canceledHashes = new Set();
  _onDemandView;

  constructor (onDemandView) {
    this._onDemandView = onDemandView;
  }

  get canceled () {
    return this._canceledHashes.has(this._onDemandView.conditionValuesSetHash);
  }

  requestCanceled (conditionValuesSetHash) {
    this._canceledHashes.add(conditionValuesSetHash || this._onDemandView.conditionValuesSetHash);
  }

  reset () {
    this._canceledHashes = new Set();
  }

}

customElements.define(CLASS_NAME, OnDemandView);

/**
 * Show the overlay as cancelled, if the conditions are in the state
 * that is canceled.
 *
 * @param refreshHandler - function to call to refresh the outer site
 * @param conditionValueSetHash - hash of the cancelled request at the
 * time the request was started. May be null when the dialog was
 * cancelled from an interactive element (then the current
 * conditionValueSetHash is used).
 */
export function displayOnDemandViewWithStateCanceled (refreshHandler, conditionValueSetHash) {
  const onDemandView = getOnDemandViewElement();
  onDemandView.handleCanceledRequest(refreshHandler, conditionValueSetHash);
}

/**
 *  explicitly mark the on demand view as outdated, because the
 * loading is finished so any change cannot be new anymore.
 */
export function setOnDemandViewStateForFinishedLoading () {
  getOnDemandViewElement()?._setStateOutdated();
}

export function getOnDemandViewElement () {
  return document.getElementsByTagName('d-on-demand-view')[0];
}
